import { IListaCbo } from "../../../models/geo-veles/IListaCbo";
import { IFormError, IFormProps } from "../../utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface ICreateListaCboFormFields {
  IdUsuario: number;
  Nome: string;
}

interface IEditListaCboFormFields {
  IdVelesGeoListaCbo: number;
  IdUsuario: number;
  Nome?: string;
  CBOs?: string[];
}

const createListaCboFormFieldsInitialState: ICreateListaCboFormFields = {
  Nome: "",
  IdUsuario: 0,
};

const editListaCboFormFieldsInitialState: IEditListaCboFormFields = {
  IdVelesGeoListaCbo: 0,
  IdUsuario: 0,
  Nome: "",
  CBOs: [],
};

interface IListaCboState {
  openDialogListaCbo: boolean;
  matchCode: {
    selectedColumn: string;
    selectedOperator: string;
    validValues: number;
  };

  fileData: {
    name: string;
    size: string;
    type: string;
    columns: string[];
    data: any[];
    validData?: any[];
    invalidData?: any[];
  };

  searchTerm: string;
  listasCbo: IListaCbo[];
  selectedListasCbo: IListaCbo[];

  fileListData: {
    fileListData: string[];
    fileListValidTable: {
      fileListValidData: string[];
      fileListInvalidData: string[];
      fileListRepeatedData: string[];
    };
  };

  createListaCboForm: IFormProps<ICreateListaCboFormFields>;
  openCreateListaCbo: boolean;
  editListaCboForm: IFormProps<IEditListaCboFormFields>;
  openEditListaCbo: boolean;
  openImportFileListaCbo: boolean;
  openPreviewFileListaCbo: boolean;
  openEditFileListaCbo: boolean;
}

const initialState: IListaCboState = {
  fileListData: {
    fileListData: [],
    fileListValidTable: {
      fileListValidData: [],
      fileListInvalidData: [],
      fileListRepeatedData: [],
    },
  },

  matchCode: {
    selectedColumn: "",
    selectedOperator: "",
    validValues: 0,
  },

  fileData: {
    name: "",
    size: "",
    type: "",
    columns: [],
    data: [],
  },
  openDialogListaCbo: false,
  listasCbo: [],
  selectedListasCbo: [],
  searchTerm: "",
  createListaCboForm: {
    errors: [],
    fields: createListaCboFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  editListaCboForm: {
    errors: [],
    fields: editListaCboFormFieldsInitialState,
    touched: false,
    delivered: false,
  },
  openCreateListaCbo: false,
  openEditListaCbo: false,
  openImportFileListaCbo: false,
  openPreviewFileListaCbo: false,
  openEditFileListaCbo: false,
};

export const validateCreateListaCboForm = (
  fields: ICreateListaCboFormFields
): IFormError<keyof ICreateListaCboFormFields>[] => {
  const errors: IFormError<keyof ICreateListaCboFormFields>[] = [];

  if (!fields.Nome?.trim()) {
    errors.push({
      key: "Nome",
      message: "Nome é obrigatório",
    });
  }

  return errors;
};

export const validateEditListaCboForm = (
  fields: IEditListaCboFormFields
): IFormError<keyof IEditListaCboFormFields>[] => {
  const errors: IFormError<keyof IEditListaCboFormFields>[] = [];

  if (!fields.Nome?.trim()) {
    errors.push({
      key: "Nome",
      message: "Nome é obrigatório",
    });
  }

  return errors;
};

const listaCboSlice = createSlice({
  name: "listaCbo",
  initialState,
  reducers: {
    setListaCboState(state, action: PayloadAction<{ listasCbo: IListaCbo[] }>) {
      const listasDisponiveis = action.payload.listasCbo.filter(
        lista => !state.selectedListasCbo.some(
          selected => selected.IdVelesGeoListaCBO === lista.IdVelesGeoListaCBO
        )
      );

      state.listasCbo = listasDisponiveis;
    },


    resetListaCboState(state) {
      state.listasCbo = initialState.listasCbo;
      state.selectedListasCbo = initialState.selectedListasCbo;
    },

    selectListaCbo(state, action: PayloadAction<{ lista: IListaCbo }>) {
      const lista = action.payload.lista;

      const jaSelecionada = state.selectedListasCbo.some(
        (item) => item.IdVelesGeoListaCBO === lista.IdVelesGeoListaCBO
      );

      if (!jaSelecionada) {
        state.selectedListasCbo.push(lista);
        state.listasCbo = state.listasCbo.filter(
          (item) => item.IdVelesGeoListaCBO !== lista.IdVelesGeoListaCBO
        );
      }
    },

    unselectListaCbo(state, action: PayloadAction<{ lista: IListaCbo }>) {
      const lista = action.payload.lista;

      state.selectedListasCbo = state.selectedListasCbo.filter(
        item => item.IdVelesGeoListaCBO !== lista.IdVelesGeoListaCBO
      );

      const jaExiste = state.listasCbo.some(
        item => item.IdVelesGeoListaCBO === lista.IdVelesGeoListaCBO
      );

      if (!jaExiste) {
        state.listasCbo.push(lista);
      }
    },

    clearSelectedListasCbo(state) {
      state.listasCbo = [...state.listasCbo, ...state.selectedListasCbo];
      state.selectedListasCbo = [];
    },

    // Create Lista CBO
    setCreateListaCboIdUsuarioState(
      state,
      action: PayloadAction<{ idUsuario: number }>
    ) {
      state.createListaCboForm.fields.IdUsuario = action.payload.idUsuario;
    },

    setCreateListaCboNomeState(state, action: PayloadAction<{ nome: string }>) {
      state.createListaCboForm.fields.Nome = action.payload.nome;
    },

    setCreateListaCboFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.createListaCboForm.touched = action.payload.touched;
    },

    setCreateListaCboFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof ICreateListaCboFormFields>[];
      }>
    ) {
      state.createListaCboForm.errors = action.payload.errors;
    },

    setCreateListaCboFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.createListaCboForm.delivered = action.payload.delivered;
    },

    resetCreateListaCboFormState(state) {
      state.createListaCboForm = initialState.createListaCboForm;
    },

    // Edit Lista CBO

    setEditListaCboIdVelesGeoListaCboState(
      state,
      action: PayloadAction<{ idVelesGeoListaCbo: number }>
    ) {
      state.editListaCboForm.fields.IdVelesGeoListaCbo =
        action.payload.idVelesGeoListaCbo;
    },

    setEditListaCboIdUsuarioState(
      state,
      action: PayloadAction<{ idUsuario: number }>
    ) {
      state.editListaCboForm.fields.IdUsuario = action.payload.idUsuario;
    },

    setEditListaCboNomeState(state, action: PayloadAction<{ nome: string }>) {
      state.editListaCboForm.fields.Nome = action.payload.nome;
    },

    setEditListaCboState(state, action: PayloadAction<{ cbos: string[] }>) {
      state.editListaCboForm.fields.CBOs = action.payload.cbos;
    },

    setAddCboEditListaCboState(
      state,
      action: PayloadAction<{ cbos: string[] }>
    ) {
      state.editListaCboForm.fields.CBOs = [
        ...(state.editListaCboForm.fields.CBOs || []),
        ...action.payload.cbos,
      ];
    },

    setEditListaCboAddState(state, action: PayloadAction<{ cbo: string }>) {
      if (!state.editListaCboForm.fields.CBOs?.includes(action.payload.cbo)) {
        state.editListaCboForm.fields.CBOs?.push(action.payload.cbo);
      }
    },

    setEditListaCboRemoveState(state, action: PayloadAction<{ cbo: string }>) {
      state.editListaCboForm.fields.CBOs =
        state.editListaCboForm.fields.CBOs?.filter(
          (item) => item !== action.payload.cbo
        ) || [];
    },

    setEditListaCboEditCboState(
      state,
      action: PayloadAction<{
        oldCbo: string;
        newCbo: string;
      }>
    ) {
      if (state.editListaCboForm.fields.CBOs?.includes(action.payload.newCbo)) {
        return;
      }
      state.editListaCboForm.fields.CBOs =
        state.editListaCboForm.fields.CBOs?.map((item) =>
          item === action.payload.oldCbo ? action.payload.newCbo : item
        ) || [];
    },

    setEditListaCboFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.editListaCboForm.touched = action.payload.touched;
    },

    setEditListaCboFormErrorsState(
      state,
      action: PayloadAction<{
        errors: IFormError<keyof IEditListaCboFormFields>[];
      }>
    ) {
      state.editListaCboForm.errors = action.payload.errors;
    },

    setEditListaCboFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.editListaCboForm.delivered = action.payload.delivered;
    },

    resetEditListaCboFormState(state) {
      state.editListaCboForm = initialState.editListaCboForm;
    },

    setListaCboFileDataListState(
      state,
      action: PayloadAction<{ values: string[] }>
    ) {
      state.fileListData.fileListData = action.payload.values;
    },

    setListaCboFileValidDataListState(
      state,
      action: PayloadAction<{ values: string[] }>
    ) {
      state.fileListData.fileListValidTable.fileListValidData =
        action.payload.values;
    },

    setListaCboFileInvalidDataListState(
      state,
      action: PayloadAction<{ values: string[] }>
    ) {
      state.fileListData.fileListValidTable.fileListInvalidData =
        action.payload.values;
    },

    setListaCboFileRepeatedDataListState(
      state,
      action: PayloadAction<{ values: string[] }>
    ) {
      state.fileListData.fileListValidTable.fileListRepeatedData =
        action.payload.values;
    },

    setListaCboSelectedColumnState(
      state,
      action: PayloadAction<{ selectedColumn: string }>
    ) {
      state.matchCode.selectedColumn = action.payload.selectedColumn;
    },

    setListaCboValidValuesState(
      state,
      action: PayloadAction<{ validValues: number }>
    ) {
      state.matchCode.validValues = action.payload.validValues;
    },

    setListaCboFileDataState(
      state,
      action: PayloadAction<{
        fileData: {
          name: string;
          size: string;
          type: string;
          columns: string[];
          data: any[];
          validData?: any[];
          invalidData?: any[];
        };
      }>
    ) {
      state.fileData.name = action.payload.fileData.name;
      state.fileData.size = action.payload.fileData.size;
      state.fileData.type = action.payload.fileData.type;
      state.fileData.columns = action.payload.fileData.columns;
      state.fileData.data = action.payload.fileData.data;
    },

    resetListaCboFileDataState(state) {
      state.fileData = { ...initialState.fileData };
      state.matchCode = { ...initialState.matchCode };
      state.fileListData = { ...initialState.fileListData };
    },

    setOpenCreateListaCboState(state, action: PayloadAction<boolean>) {
      state.openCreateListaCbo = action.payload;
    },

    setOpenEditListaCboState(state, action: PayloadAction<boolean>) {
      state.openEditListaCbo = action.payload;
    },

    setOpenImportFileCboState(state, action: PayloadAction<boolean>) {
      state.openImportFileListaCbo = action.payload;
    },

    setOpenPreviewListaCboState(state, action: PayloadAction<boolean>) {
      state.openPreviewFileListaCbo = action.payload;
    },

    setOpenEditCboState(state, action: PayloadAction<boolean>) {
      state.openEditFileListaCbo = action.payload;
    },

    setOpenDialogListaCboState(state, action: PayloadAction<boolean>) {
      state.openDialogListaCbo = action.payload;
    },
  },
});

export const {
  setListaCboState,
  resetListaCboState,
  selectListaCbo,
  unselectListaCbo,
  clearSelectedListasCbo,

  // Create Lista CBO
  setOpenCreateListaCboState,
  setCreateListaCboIdUsuarioState,
  setCreateListaCboNomeState,
  setCreateListaCboFormTouchedState,
  setCreateListaCboFormErrorsState,
  setCreateListaCboFormDeliveredState,
  resetCreateListaCboFormState,
  // Edit Lista CBO
  setEditListaCboIdVelesGeoListaCboState,
  setEditListaCboIdUsuarioState,
  setEditListaCboNomeState,
  setEditListaCboState,
  setAddCboEditListaCboState,
  setEditListaCboAddState,
  setEditListaCboRemoveState,
  setEditListaCboEditCboState,
  setEditListaCboFormTouchedState,
  setEditListaCboFormErrorsState,
  setEditListaCboFormDeliveredState,
  resetEditListaCboFormState,
  // File Data
  setListaCboFileDataListState,
  setListaCboFileValidDataListState,
  setListaCboFileInvalidDataListState,
  setListaCboFileRepeatedDataListState,
  setListaCboSelectedColumnState,
  setListaCboValidValuesState,
  setListaCboFileDataState,
  resetListaCboFileDataState,
  // Dialogs
  setOpenDialogListaCboState,
  setOpenEditListaCboState,
  setOpenImportFileCboState,
  setOpenPreviewListaCboState,
  setOpenEditCboState,
} = listaCboSlice.actions;

export const listaCboReducer = listaCboSlice.reducer;